<script setup lang="ts">
import { ref, computed, watch } from 'vue';
import { useProductStore } from '../stores';
import { useLaboratoryStore } from '../stores';
import { useCategoryStore } from '../stores/categoryStore';
import { useNotificationStore } from '../stores/notificationStore';
import type { ProductWithDetails } from '../types';
import SortableTable from './SortableTable.vue';

// Stores
const productStore = useProductStore();
const laboratoryStore = useLaboratoryStore();
const notificationStore = useNotificationStore();
const categoryStore = useCategoryStore();

// Estados del componente
const selectedLab = ref('');
const selectedCategory = ref('');
const editedPrices = ref<Record<string, number>>({});
const editedFactors = ref<Record<string, number>>({});
const error = ref('');
const processing = ref(false);
const sortBy = ref('Nombre');
const sortDir = ref<'asc' | 'desc'>('asc');
const searchTerm = ref('');

// Watch for changes in edited prices to update composite products
watch([editedPrices, editedFactors], async () => {
  if (!selectedLab.value) return;

  // Get ALL composite products regardless of category
  const compositeProducts = productStore.products.filter(product => {
    const lab = product.laboratories.find(l => l.laboratory.id === selectedLab.value);
    return lab?.details.isCompuesto && lab.details.componentes?.length;
  });

  // Update each composite product if any of its components changed
  for (const product of compositeProducts) {
    const lab = product.laboratories.find(l => l.laboratory.id === selectedLab.value);
    if (!lab?.details.componentes) continue;

    // Check if any component was modified
    const hasModifiedComponent = lab.details.componentes.some(componentId => 
      componentId in editedPrices.value
    );

    if (hasModifiedComponent) {
      // Calculate new total price from components
      const totalPrice = lab.details.componentes.reduce((total, componentId) => {
        const componentProduct = productStore.products.find(p => p.id === componentId);
        const componentLab = componentProduct?.laboratories.find(l => 
          l.laboratory.id === selectedLab.value
        );
        
        // Use edited price if available, otherwise use original price
        const componentPrice = editedPrices.value[componentId] ?? 
          componentLab?.details.PrecioDeCosto ?? 0;
        
        return total + componentPrice;
      }, 0);

      // Update the composite product's price
      editedPrices.value[product.id!] = totalPrice;
    }
  }
}, { deep: true });

// Obtener detalles del laboratorio para un producto
const getProductLaboratory = (product: ProductWithDetails) => {
  return product.laboratories.find(lab => lab.laboratory.id === selectedLab.value);
};

// Calcular precio de venta
const calculateSalePrice = (product: ProductWithDetails) => {
  const lab = getProductLaboratory(product);
  if (!lab) return 0;
  
  const price = editedPrices.value[product.id!] ?? lab.details.PrecioDeCosto;
  const factor = editedFactors.value[product.id!] ?? product.Factor;
  
  return price * factor;
};

// Manejar cambio de precio
const handlePriceChange = (productId: string, newPrice: number) => {
  editedPrices.value[productId] = newPrice;
};

// Manejar cambio de factor
const handleFactorChange = (productId: string, newFactor: number) => {
  editedFactors.value[productId] = newFactor;
};

// Productos filtrados y ordenados
const sortedProducts = computed(() => {
  const filtered = productStore.products
    .filter(product => {
      const matchesLab = product.laboratories.some(lab => 
        lab.laboratory.id === selectedLab.value
      );
      
      const matchesCategory = !selectedCategory.value || 
        product.Categoria === selectedCategory.value;

      const matchesSearch = !searchTerm.value || 
        product.Nombre.toLowerCase().includes(searchTerm.value.toLowerCase());

      return matchesLab && matchesCategory && matchesSearch;
    })
    .map(product => {
      const lab = getProductLaboratory(product);
      return {
        ...product,
        PrecioActual: lab?.details.PrecioDeCosto || 0,
        PrecioVenta: calculateSalePrice(product),
        CategoriaLabel: categoryStore.categories[product.Categoria]
      };
    });

  return [...filtered].sort((a, b) => {
    let aValue, bValue;

    switch (sortBy.value) {
      case 'PrecioActual':
        aValue = a.PrecioActual;
        bValue = b.PrecioActual;
        break;
      case 'PrecioVenta':
        aValue = calculateSalePrice(a);
        bValue = calculateSalePrice(b);
        break;
      case 'CategoriaLabel':
        aValue = a.CategoriaLabel.toLowerCase();
        bValue = b.CategoriaLabel.toLowerCase();
        break;
      default:
        aValue = (a as any)[sortBy.value]?.toString().toLowerCase();
        bValue = (b as any)[sortBy.value]?.toString().toLowerCase();
    }

    if (aValue < bValue) return sortDir.value === 'asc' ? -1 : 1;
    if (aValue > bValue) return sortDir.value === 'asc' ? 1 : -1;
    return 0;
  });
});

// Guardar cambios
const saveChanges = async () => {
  try {
    const hasChanges = Object.keys(editedPrices.value).length > 0 || 
                      Object.keys(editedFactors.value).length > 0;
    
    if (!hasChanges) {
      error.value = 'No hay cambios para guardar';
      return;
    }

    processing.value = true;
    let updatedCount = 0;

    // Get all products that need updating (including composite products from other categories)
    const productsToUpdate = productStore.products.filter(product => {
      const hasDirectChanges = product.id! in editedPrices.value || 
                             product.id! in editedFactors.value;
      
      const lab = product.laboratories.find(l => l.laboratory.id === selectedLab.value);
      const isComposite = lab?.details.isCompuesto && lab.details.componentes?.length;
      
      return hasDirectChanges || isComposite;
    });

    // First update non-composite products
    for (const product of productsToUpdate) {
      const lab = product.laboratories.find(l => l.laboratory.id === selectedLab.value);
      if (!lab || lab.details.isCompuesto) continue;

      const hasProductChanges = product.id! in editedPrices.value || 
                               product.id! in editedFactors.value;
      
      if (!hasProductChanges) continue;

      const updatedProduct = {
        ...product,
        Factor: editedFactors.value[product.id!] ?? product.Factor,
        laboratories: product.laboratories.map(lab => {
          if (lab.laboratory.id === selectedLab.value) {
            return {
              ...lab,
              details: {
                ...lab.details,
                PrecioDeCosto: editedPrices.value[product.id!] ?? lab.details.PrecioDeCosto,
                UltimaActualizacion: new Date()
              }
            };
          }
          return lab;
        })
      };

      await productStore.updateProduct(product.id!, updatedProduct);
      updatedCount++;
    }

    // Then update composite products
    for (const product of productsToUpdate) {
      const lab = product.laboratories.find(l => l.laboratory.id === selectedLab.value);
      if (!lab?.details.isCompuesto) continue;

      const hasProductChanges = product.id! in editedPrices.value;
      if (!hasProductChanges) continue;

      const updatedProduct = {
        ...product,
        laboratories: product.laboratories.map(lab => {
          if (lab.laboratory.id === selectedLab.value) {
            return {
              ...lab,
              details: {
                ...lab.details,
                PrecioDeCosto: editedPrices.value[product.id!] ?? lab.details.PrecioDeCosto,
                UltimaActualizacion: new Date()
              }
            };
          }
          return lab;
        })
      };

      await productStore.updateProduct(product.id!, updatedProduct);
      updatedCount++;
    }

    notificationStore.success(`Se actualizaron ${updatedCount} productos correctamente`);
    editedPrices.value = {};
    editedFactors.value = {};
    error.value = '';
  } catch (err) {
    error.value = err instanceof Error ? err.message : 'Error al guardar los cambios';
    notificationStore.error('Error al actualizar los productos');
  } finally {
    processing.value = false;
  }
};
</script>

<template>
  <div class="w-full">
    <h2 class="text-2xl font-bold text-slate-800 mb-6">Modificación Manual de Precios</h2>
    
    <div class="space-y-6">
      <!-- Filters section -->
      <div class="grid grid-cols-1 md:grid-cols-3 gap-4">
        <!-- Search input -->
        <div class="relative">
          <div class="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
            <svg class="h-5 w-5 text-slate-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
            </svg>
          </div>
          <input
            v-model="searchTerm"
            type="text"
            placeholder="Buscar productos..."
            class="input-field pl-10"
          />
        </div>

        <!-- Laboratory selector -->
        <div>
          <select v-model="selectedLab" class="input-field">
            <option value="">Seleccionar laboratorio</option>
            <option 
              v-for="lab in laboratoryStore.laboratories" 
              :key="lab.id" 
              :value="lab.id"
            >
              {{ lab.Nombre }}
            </option>
          </select>
        </div>

        <!-- Category selector -->
        <div>
          <select v-model="selectedCategory" class="input-field">
            <option value="">Todas las categorías</option>
            <option 
              v-for="[id, label] in Object.entries(categoryStore.categories)" 
              :key="id" 
              :value="id"
            >
              {{ label }}
            </option>
          </select>
        </div>
      </div>

      <div v-if="error" class="bg-red-50 text-red-600 p-3 rounded-lg text-sm">
        {{ error }}
      </div>

      <!-- Products table -->
      <div v-if="sortedProducts.length > 0" class="mt-8">
        <h3 class="text-lg font-semibold text-slate-800 mb-4">Vista Previa de Cambios</h3>
        <div class="overflow-x-auto rounded-lg border border-slate-200">
          <table class="w-full border-collapse">
            <thead>
              <tr class="bg-slate-50">
                <th class="px-4 py-3 text-left text-sm font-medium text-slate-700">Producto</th>
                <th class="px-4 py-3 text-left text-sm font-medium text-slate-700">Categoría</th>
                <th class="px-4 py-3 text-right text-sm font-medium text-slate-700">Precio Actual</th>
                <th class="px-4 py-3 text-right text-sm font-medium text-slate-700">Nuevo Precio</th>
                <th class="px-4 py-3 text-right text-sm font-medium text-slate-700">Factor</th>
                <th class="px-4 py-3 text-right text-sm font-medium text-slate-700">Precio de Venta</th>
              </tr>
            </thead>
            <tbody class="divide-y divide-slate-200">
              <tr v-for="product in sortedProducts" 
                  :key="product.id"
                  :class="[
                    'hover:bg-slate-50 transition-colors',
                    { 'bg-slate-50': product.laboratories.some(lab => lab.details.isCompuesto) }
                  ]"
              >
                <td class="px-4 py-2 text-sm font-medium text-slate-900">
                  {{ product.Nombre }}
                  <span v-if="product.laboratories.some(lab => lab.details.isCompuesto)" 
                        class="ml-2 text-xs text-indigo-600 font-normal">
                    (Precio compuesto)
                  </span>
                </td>
                <td class="px-4 py-2 text-sm text-slate-600">{{ product.CategoriaLabel }}</td>
                <td class="px-4 py-2 text-sm text-slate-600 text-right">
                  ${{ product.PrecioActual.toLocaleString() }}
                </td>
                <td class="px-4 py-2">
                  <input
                    type="number"
                    :value="editedPrices[product.id!] ?? product.PrecioActual"
                    @input="e => handlePriceChange(product.id!, +(e.target as HTMLInputElement).value)"
                    class="input-field w-32 text-right"
                    min="0"
                    step="0.01"
                    :disabled="product.laboratories.some(lab => lab.details.isCompuesto)"
                    :class="{ 'bg-slate-100': product.laboratories.some(lab => lab.details.isCompuesto) }"
                  />
                </td>
                <td class="px-4 py-2">
                  <input
                    type="number"
                    :value="editedFactors[product.id!] ?? product.Factor"
                    @input="e => handleFactorChange(product.id!, +(e.target as HTMLInputElement).value)"
                    class="input-field w-24 text-right"
                    min="0"
                    step="0.01"
                    :disabled="product.laboratories.some(lab => lab.details.isCompuesto)"
                    :class="{ 'bg-slate-100': product.laboratories.some(lab => lab.details.isCompuesto) }"
                  />
                </td>
                <td class="px-4 py-2 text-sm font-medium text-indigo-600 text-right">
                  ${{ Math.round(calculateSalePrice(product)).toLocaleString() }}
                </td>
              </tr>
            </tbody>
          </table>
        </div>
      </div>

      <div class="flex justify-end mt-6">
        <button 
          @click="saveChanges" 
          class="btn-primary"
          :disabled="processing || sortedProducts.length === 0 || 
            (!Object.keys(editedPrices).length && !Object.keys(editedFactors).length)"
        >
          {{ processing ? 'Guardando...' : 'Guardar Cambios' }}
        </button>
      </div>
    </div>
  </div>
</template>

<style scoped>
.input-field {
  @apply w-full p-2 border border-slate-200 rounded-lg focus:ring-2 focus:ring-indigo-500 
         focus:border-indigo-500 outline-none transition-all duration-200;
}
</style>